import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/components/LanguageContext';

export default function Navbar() {
  const { t } = useTranslation();
  const { lang, setLang } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const navLinks = [
    { label: t('nav.videos'), href: '#videos' },
    { label: t('nav.gallery'), href: '#gallery' },
    { label: t('nav.services'), href: '#services' },
    { label: t('nav.contact'), href: '#salon' },
  ];

  const langs: { key: 'tr' | 'en' | 'ru'; label: string }[] = [
    { key: 'tr', label: 'TR' },
    { key: 'en', label: 'EN' },
    { key: 'ru', label: 'RU' },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-white/90 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <a href="#" className="flex items-center gap-3 flex-shrink-0">
            <img
              src="https://static.readdy.ai/image/63ce3e67fd2d5effcfb662b2683f46ed/99522d9a3a4519144bb99e3bc03ec0b1.jpeg"
              alt="Cemil Kuaför Logo"
              className="h-10 w-10 md:h-12 md:w-12 rounded-full object-cover"
            />
            <span
              className={`font-display text-base md:text-lg font-semibold tracking-tight transition-colors ${
                scrolled ? 'text-dark-950' : 'text-white'
              }`}
            >
              {t('nav.brand')}
            </span>
          </a>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className={`text-sm font-medium transition-colors hover:text-champagne-600 whitespace-nowrap ${
                  scrolled ? 'text-dark-800' : 'text-white/90'
                }`}
              >
                {link.label}
              </a>
            ))}

            {/* Language Switcher */}
            <div className="flex items-center gap-1 bg-white/10 rounded-full p-1">
              {langs.map((l) => (
                <button
                  key={l.key}
                  onClick={() => setLang(l.key)}
                  className={`px-3 py-1 rounded-full text-xs font-semibold transition-all whitespace-nowrap cursor-pointer ${
                    lang === l.key
                      ? scrolled
                        ? 'bg-dark-950 text-white'
                        : 'bg-white text-dark-950'
                      : scrolled
                        ? 'text-dark-600 hover:text-dark-950'
                        : 'text-white/70 hover:text-white'
                  }`}
                >
                  {l.label}
                </button>
              ))}
            </div>
          </div>

          {/* Mobile hamburger */}
          <button
            className="md:hidden flex items-center justify-center w-10 h-10 cursor-pointer"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            <i
              className={`ri-menu-line text-xl ${scrolled ? 'text-dark-950' : 'text-white'}`}
            />
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="md:hidden bg-white/95 backdrop-blur-lg border-t border-dark-100 animate-fade-in">
          <div className="px-4 py-4 space-y-3">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={() => setMenuOpen(false)}
                className="block text-dark-800 text-sm font-medium py-2"
              >
                {link.label}
              </a>
            ))}
            <div className="flex items-center gap-2 pt-2 border-t border-dark-100">
              {langs.map((l) => (
                <button
                  key={l.key}
                  onClick={() => {
                    setLang(l.key);
                    setMenuOpen(false);
                  }}
                  className={`px-4 py-2 rounded-full text-xs font-semibold transition-all whitespace-nowrap cursor-pointer ${
                    lang === l.key
                      ? 'bg-dark-950 text-white'
                      : 'text-dark-600 bg-dark-50'
                  }`}
                >
                  {l.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}